// tickets-by-type-chart.tsx — Tickets by type group grouped bar chart.
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { TicketsByType } from '@/api/tickets-statistics';

interface Props {
  data: TicketsByType[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Renders one bar per ticket_type_group. Each bar's height = group_total.
 * A single flat series is used since groups are categorically distinct.
 */
export function TicketsByTypeChart({ data, isLoading, isError }: Props) {
  const rows = (data ?? [])
    .filter((d) => d.group_total > 0)
    .sort((a, b) => b.group_total - a.group_total)
    .map((d) => ({
      label: d.ticket_type_group.replace(/_/g, ' '),
      total: d.group_total,
    }));

  const series = [{ key: 'total', label: 'Tickets', color: CHART_COLORS[0] }];

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Tickets by Type Group</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load type data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 240 }} />
        ) : rows.length === 0 ? (
          <EmptyState title="No data" description="No ticket type data in this date range." />
        ) : (
          <Chart
            type="bar"
            data={rows}
            xKey="label"
            series={series}
            height={240}
            showGrid
            showXAxis
            showYAxis
            valueFormatter={(v) => String(v ?? 0)}
          />
        )}
      </CardBody>
    </Card>
  );
}
